'use client';

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import Link from 'next/link';
import { useParams, useRouter, usePathname, useSearchParams } from 'next/navigation';
import { motion } from 'framer-motion';
import {
  MapPin, Star, CheckCircle, Building2, Music,
  Phone, Mail, Instagram, ArrowLeft, Eye,
  Calendar, ArrowRight, ChevronRight, Share2, BadgeCheck,
  ExternalLink, Crown, Award, X, Loader2, Facebook, Youtube,
  Clock, Camera, MessageCircle, Sparkles, CalendarDays
} from 'lucide-react';
import PublicEditorialChrome from '@/components/shared/PublicEditorialChrome';
import { DirectoryPromoBannerB2C } from '@/components/directory/DirectoryPromoBannerB2C';
import { DirectoryPromoBannerB2B } from '@/components/directory/DirectoryPromoBannerB2B';
import { LeadCaptureRedirectModal, type LeakRedirect } from '@/components/directory/LeadCaptureRedirectModal';
import { DirectoryPhoto } from '@/components/directory/DirectoryPhoto';
import {
  trackLeakCaptureOpen,
  trackDirectoryCtaClicked,
  trackPdpViewed,
  trackClaimStarted,
  trackClaimCompleted,
} from '@/lib/analytics';
import { LANDING_COLORS } from '@/constants/landing-colors';
import { MAPBOX_TOKEN } from '@/lib/mapbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { getCaptcha } from '@/lib/captcha';
import { trackDirectoryFormStage, trackDirectoryFormSubmission } from '@/lib/directory-form-analytics';
import { TYPE_COLORS, TYPE_ICONS, TYPE_LABELS, type DirectoryType } from '@/constants/directoryTypes';
import { getAppUrl } from '@/utils/domain';

interface ListingPhoto {
  url: string;
  alt?: string;
  caption?: string;
  type?: 'exterior' | 'interior' | 'class' | 'team' | 'other';
}

interface ListingHoursInterval {
  open: string;
  close: string;
}

type ListingDayHours = ListingHoursInterval | ListingHoursInterval[] | null;

interface ListingHours {
  mon?: ListingDayHours;
  tue?: ListingDayHours;
  wed?: ListingDayHours;
  thu?: ListingDayHours;
  fri?: ListingDayHours;
  sat?: ListingDayHours;
  sun?: ListingDayHours;
}

function getHoursIntervals(dayHours: ListingDayHours | undefined): ListingHoursInterval[] {
  if (!dayHours) return [];
  return Array.isArray(dayHours) ? dayHours : [dayHours];
}

export interface ListingDetail {
  id: string;
  name: string;
  slug: string;
  type: DirectoryType;
  category?: string | null;
  services?: string[] | null;
  address: string;
  city: string;
  province: string;
  postal_code: string | null;
  latitude: number | null;
  longitude: number | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  instagram: string | null;
  facebook: string | null;
  youtube: string | null;
  whatsapp: string | null;
  google_maps_url: string | null;
  rating: number | null;
  review_count: number;
  observations: string | null;
  instruments: string[] | null;
  photos: ListingPhoto[] | null;
  hours: ListingHours | null;
  price_range: string | null;
  price_source_url?: string | null;
  price_verified_at?: string | null;
  logo_url: string | null;
  is_claimed: boolean;
  is_premium: boolean;
  is_promoted: boolean;
  is_verified: boolean;
  premium_plan: string | null;
  view_count: number;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface RelatedListing {
  id: string;
  name: string;
  slug: string;
  type: string;
  city: string;
  rating: number | null;
  review_count: number;
  is_claimed: boolean;
}

const DAY_NAMES: Record<string, string> = {
  mon: 'Lunes',
  tue: 'Martes',
  wed: 'Miércoles',
  thu: 'Jueves',
  fri: 'Viernes',
  sat: 'Sábado',
  sun: 'Domingo',
};

const DIRECTORY_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

function hasListingHours(hours: ListingHours | null): hours is ListingHours {
  return Boolean(hours && DIRECTORY_DAYS.some((day) => getHoursIntervals(hours[day]).length > 0));
}

function DirectoryHoursSchedule({ hours }: { hours: ListingHours }) {
  return (
    <div className="space-y-2">
      {DIRECTORY_DAYS.map((day) => {
        const intervals = getHoursIntervals(hours[day]);
        const isToday = new Date().toLocaleDateString('en-US', { weekday: 'short' }).toLowerCase().startsWith(day.slice(0, 2));

        return (
          <div
            key={day}
            className={`flex items-center justify-between gap-4 rounded-lg px-3 py-2 ${
              isToday ? 'bg-white shadow-sm' : ''
            }`}
          >
            <span className={`text-sm ${isToday ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
              {DAY_NAMES[day]}
              {isToday && <span className="ml-2 text-xs text-emerald-600">(Hoy)</span>}
            </span>
            {intervals.length > 0 ? (
              <span className={`text-right text-sm ${isToday ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                {intervals.map(({ open, close }) => `${open} - ${close}`).join(' y ')}
              </span>
            ) : (
              <span className="text-sm text-gray-400">Cerrado</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function formatUpdatedDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

const INSTRUMENT_LABELS: Record<string, string> = {
  piano: 'Piano',
  guitarra: 'Guitarra',
  guitar: 'Guitarra',
  canto: 'Canto',
  singing: 'Canto',
  bateria: 'Batería',
  drums: 'Batería',
  violin: 'Violín',
  bajo: 'Bajo',
  bass: 'Bajo',
  ukulele: 'Ukulele',
  saxofon: 'Saxofón',
  sax: 'Saxofón',
  flauta: 'Flauta',
  flute: 'Flauta',
  trompeta: 'Trompeta',
  trumpet: 'Trompeta',
  clarinete: 'Clarinete',
  percusion: 'Percusión',
  lenguaje_musical: 'Lenguaje Musical',
  teoria: 'Teoría',
};

const API_BASE = process.env.NEXT_PUBLIC_API_URL || 'https://pjmuiftjquxmvonpiwxu.supabase.co/functions/v1/api';
const colors = LANDING_COLORS.jams;
// Identifies the exact disclosure accepted before Jamsom can relay a request.
const DIRECTORY_LEAD_DISCLOSURE_VERSION = 'directory_contact_sharing_v1';

// Genera un número de visitas pseudoaleatorio pero estable basado en el ID
function getDisplayViewCount(id: string, baseCount: number): number {
  if (baseCount > 100) return baseCount;
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    const char = id.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash % 106) + 45;
}

function hasRecentPriceEvidence(listing: ListingDetail): boolean {
  if (!listing.price_range || !listing.price_source_url || !listing.price_verified_at) return false;
  try {
    const source = new URL(listing.price_source_url);
    const verifiedAt = new Date(listing.price_verified_at).getTime();
    const ageMs = Date.now() - verifiedAt;
    return (source.protocol === 'https:' || source.protocol === 'http:')
      && Number.isFinite(verifiedAt) && ageMs >= 0 && ageMs <= 180 * 24 * 60 * 60 * 1000;
  } catch { return false; }
}

function publicFormError(error?: string): string {
  const messages: Record<string, string> = {
    captcha_required: 'Completa la verificación anti-spam antes de enviar.',
    captcha_unavailable: 'La verificación anti-spam no está disponible. Inténtalo más tarde.',
    captcha_failed: 'No pudimos validar la verificación anti-spam. Inténtalo de nuevo.',
    captcha_provider_invalid: 'La protección anti-spam se está actualizando. Recarga la página e inténtalo de nuevo.',
    too_many_requests: 'Has enviado varias solicitudes. Espera un rato antes de volver a intentarlo.',
    rate_limit_unavailable: 'No podemos procesar solicitudes ahora. Inténtalo más tarde.',
    payload_too_large: 'El mensaje es demasiado largo. Acórtalo e inténtalo de nuevo.',
    listing_not_found: 'Esta ficha ya no está disponible.',
    invalid_fields: 'Revisa los campos obligatorios e inténtalo de nuevo.',
    sharing_authorization_required: 'Confirma la autorización para poder enviar tu consulta.',
  };
  return messages[error || ''] || 'No pudimos enviar tu solicitud. Inténtalo de nuevo.';
}

interface DirectoryDetailProps {
  initialListing?: ListingDetail | null;
  initialRelated?: RelatedListing[];
}

interface DirectoryBookingResponse {
  success?: boolean;
  id?: string;
  error?: string;
  routing_mode?: 'claimed_direct' | 'unclaimed_assisted';
  delivery_status?: 'awaiting_review' | 'queued' | 'sent' | 'failed';
}

export default function DirectoryDetail({ initialListing, initialRelated }: DirectoryDetailProps = {}) {
  const params = useParams<{ slug: string }>();
  const slug = params.slug;
  const router = useRouter();
  const pathname = usePathname();
  const [listing, setListing] = useState<ListingDetail | null>(initialListing || null);
  const [relatedListings, setRelatedListings] = useState<RelatedListing[]>(initialRelated || []);
  const [loading, setLoading] = useState(!initialListing);
  const [error, setError] = useState<string | null>(null);

  // Claim modal state
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [claimForm, setClaimForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [claimSubmitting, setClaimSubmitting] = useState(false);
  const [claimSuccess, setClaimSuccess] = useState(false);
  const [claimError, setClaimError] = useState('');

  // Deep-link support: /reclama-tu-ficha links here with ?claim=1 to open the
  // claim dialog straight away for owners arriving from the claim landing.
  const searchParams = useSearchParams();
  const claimRequestedFromLink = searchParams.get('claim') === '1' && listing?.is_claimed === false;

  // Booking state
  const [bookingForm, setBookingForm] = useState({
    name: '',
    email: '',
    phone: '',
    interest: '',
    message: ''
  });
  const [bookingSubmitting, setBookingSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingRoutingMode, setBookingRoutingMode] = useState<DirectoryBookingResponse['routing_mode'] | null>(null);
  const [bookingDeliveryStatus, setBookingDeliveryStatus] = useState<DirectoryBookingResponse['delivery_status'] | null>(null);
  const [bookingError, setBookingError] = useState('');
  const [sharingAuthorizationOpen, setSharingAuthorizationOpen] = useState(false);
  const startedForms = useRef(new Set<'booking' | 'claim'>());

  // Tabs state
  const [activeTab, setActiveTab] = useState<'info' | 'fotos' | 'horarios'>('info');

  // Intercepción de salidas del funnel: capturamos email antes de redirigir fuera
  const [leakRedirect, setLeakRedirect] = useState<LeakRedirect | null>(null);
  const listingId = listing?.id;
  const listingType = listing?.type;
  const listingCity = listing?.city;
  const listingClaimed = listing?.is_claimed;
  const listingPremium = (listing as { is_premium?: boolean } | null)?.is_premium;

  const fetchListing = useCallback(async (signal: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/directory/${slug}`, { signal });
      const data = await res.json();
      if (signal.aborted) return;

      if (!res.ok || data.error) {
        setError(data.error || 'Listing not found');
        setListing(null);
      } else {
        setListing(data.listing);
      }
    } catch (err) {
      if (signal.aborted) return;
      console.error('Error fetching listing:', err);
      setError('Error loading listing');
      setListing(null);
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [setError, setListing, setLoading, slug]);

  const fetchRelatedListings = useCallback(async (listingToMatch: ListingDetail, signal: AbortSignal) => {
    try {
      const params = new URLSearchParams({
        type: listingToMatch.type,
        city: listingToMatch.city,
        limit: '3',
        exclude: listingToMatch.id,
      });
      const res = await fetch(`${API_BASE}/directory?${params}`, { signal });
      const data = await res.json();
      if (!signal.aborted && data.listings) {
        setRelatedListings(data.listings.slice(0, 3));
      }
    } catch (err) {
      if (!signal.aborted) console.error('Error fetching related:', err);
    }
  }, [setRelatedListings]);

  const handleClaimModalOpenChange = useCallback((open: boolean) => {
    setClaimModalOpen(open);
    if (!open && claimRequestedFromLink) {
      const nextSearchParams = new URLSearchParams(searchParams.toString());
      nextSearchParams.delete('claim');
      const query = nextSearchParams.toString();
      router.replace(query ? `${pathname}?${query}` : pathname);
    }
  }, [claimRequestedFromLink, pathname, router, searchParams]);

  // Scroll to top when navigating to a new listing
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Analytics: ficha vista (PDP). Base del funnel lead-gen (PLP→PDP) y del claim.
  useEffect(() => {
    if (!listingId || !listingType || !listingCity || listingClaimed === undefined) return;
    trackPdpViewed({
      listing_id: listingId,
      listing_type: listingType,
      city: listingCity,
      is_premium: listingPremium,
      is_claimed: listingClaimed,
    });
  }, [listingId, listingType, listingCity, listingClaimed, listingPremium]);

  // Analytics: apertura del flujo de reclamar ficha (por botón o deep-link ?claim=1).
  useEffect(() => {
    if ((claimModalOpen || claimRequestedFromLink) && listingId && listingType) {
      trackClaimStarted(
        { listing_id: listingId, listing_type: listingType },
        searchParams.get('claim') === '1' ? 'deep_link' : 'pdp',
      );
    }
  }, [claimModalOpen, claimRequestedFromLink, listingId, listingType, searchParams]);

  useEffect(() => {
    if (slug && !initialListing) {
      const controller = new AbortController();
      const startRequest = window.setTimeout(() => {
        void fetchListing(controller.signal);
      }, 0);
      return () => {
        window.clearTimeout(startRequest);
        controller.abort();
      };
    }
  }, [fetchListing, initialListing, slug]);

  useEffect(() => {
    if (listing && !initialRelated?.length) {
      const controller = new AbortController();
      const startRequest = window.setTimeout(() => {
        void fetchRelatedListings(listing, controller.signal);
      }, 0);
      return () => {
        window.clearTimeout(startRequest);
        controller.abort();
      };
    }
  }, [fetchRelatedListings, initialRelated?.length, listing]);

  async function trackClick(type: string) {
    if (!listing) return;
    try {
      await fetch(`${API_BASE}/directory/${listing.id}/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type }),
      });
    } catch (err) {
      console.error('Track click error:', err);
    }
  }

  /**
   * Intercepta un CTA externo: evita la navegación nativa, registra el click
   * (compat con el tracking del BFF) y abre el modal de captura de email antes
   * de redirigir fuera del ecosistema.
   */
  function interceptExternal(
    e: React.MouseEvent,
    url: string,
    type: LeakRedirect['type'],
    trackType: string,
  ) {
    e.preventDefault();
    if (!listing) return;
    trackClick(trackType);
    // Intención de contacto (paso PDP→CTA del funnel lead-gen).
    trackDirectoryCtaClicked(
      { listing_id: listing.id, is_premium: (listing as { is_premium?: boolean }).is_premium },
      type,
    );
    trackLeakCaptureOpen(listing.id, type);
    setLeakRedirect({ url, type });
  }

  function handleBack() {
    // In Next.js we don't have location.state, so just navigate to directory
    if (listing) {
      router.push(`/directorio?type=${listing.type}`);
    } else {
      router.push('/directorio');
    }
  }

  async function handleClaimSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!listing) return;

    setClaimSubmitting(true);
    setClaimError('');
    trackDirectoryFormStage('claim', 'submit_attempt', { listingId: listing.id, listingType: listing.type });
    try {
      const captcha = await getCaptcha('submit_claim');

      const res = await fetch(`${API_BASE}/directory/${listing.id}/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...claimForm,
          listing_id: listing.id,
          listing_name: listing.name,
          captcha_token: captcha.token,
          captcha_provider: captcha.provider,
          // Alias temporal para instalaciones backend anteriores.
          recaptcha_token: captcha.provider === 'recaptcha' ? captcha.token : undefined,
        }),
      });

      const result = await res.json().catch(() => null) as { success?: boolean; id?: string; error?: string } | null;
      if (res.ok && result?.success && result.id) {
        setClaimSuccess(true);
        trackClick('claim_submit');
        trackDirectoryFormSubmission('claim', 'success', { listingId: listing.id, listingType: listing.type });
        // Analytics: reclamación completada — activación del dueño de la ficha.
        trackClaimCompleted({ listing_id: listing.id });
      } else {
        setClaimError(publicFormError(result?.error));
        trackDirectoryFormSubmission('claim', 'failure', { listingId: listing.id, listingType: listing.type, reason: 'api_error' });
      }
    } catch (err) {
      console.error('Claim submit error:', err);
      setClaimError(err instanceof Error ? err.message : 'No pudimos enviar tu solicitud. Inténtalo de nuevo.');
      trackDirectoryFormSubmission('claim', 'failure', { listingId: listing.id, listingType: listing.type, reason: 'request_error' });
    }
    setClaimSubmitting(false);
  }

  async function submitBooking() {
    if (!listing) return;

    setBookingSubmitting(true);
    setBookingError('');
    trackDirectoryFormStage('booking', 'submit_attempt', { listingId: listing.id, listingType: listing.type });
    try {
      const captcha = await getCaptcha('submit_booking');

      const res = await fetch(`${API_BASE}/directory/${listing.id}/booking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...bookingForm,
          listing_id: listing.id,
          listing_name: listing.name,
          listing_type: listing.type,
          sharing_authorized: true,
          disclosure_version: DIRECTORY_LEAD_DISCLOSURE_VERSION,
          captcha_token: captcha.token,
          captcha_provider: captcha.provider,
          recaptcha_token: captcha.provider === 'recaptcha' ? captcha.token : undefined,
        }),
      });

      const result = await res.json().catch(() => null) as DirectoryBookingResponse | null;
      if (res.ok && result?.success && result.id) {
        setBookingRoutingMode(result.routing_mode || null);
        setBookingDeliveryStatus(result.delivery_status || null);
        setBookingSuccess(true);
        setSharingAuthorizationOpen(false);
        trackClick('booking_submit');
        trackDirectoryFormSubmission('booking', 'success', { listingId: listing.id, listingType: listing.type });
      } else {
        setBookingError(publicFormError(result?.error));
        trackDirectoryFormSubmission('booking', 'failure', { listingId: listing.id, listingType: listing.type, reason: 'api_error' });
      }
    } catch (err) {
      console.error('Booking submit error:', err);
      setBookingError(err instanceof Error ? err.message : 'No pudimos enviar tu consulta. Inténtalo de nuevo.');
      trackDirectoryFormSubmission('booking', 'failure', { listingId: listing.id, listingType: listing.type, reason: 'request_error' });
    }
    setBookingSubmitting(false);
  }

  function handleBookingSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!listing) return;

    if (!listing.is_claimed) {
      setSharingAuthorizationOpen(true);
      return;
    }

    void submitBooking();
  }

  function trackFormStarted(form: 'booking' | 'claim') {
    if (!listing || startedForms.current.has(form)) return;
    startedForms.current.add(form);
    trackDirectoryFormStage(form, 'started', { listingId: listing.id, listingType: listing.type });
  }

  const handleDirectoryChromeAction = useCallback(() => {
    if (listing && !listing.is_claimed) {
      setClaimModalOpen(true);
      return;
    }
    router.push('/reclama-tu-ficha');
  }, [listing, router]);

  const renderWithEditorialChrome = (content: ReactNode) => (
    <PublicEditorialChrome
      variant="directorio"
      actionLabel="Añadir mi negocio"
      onAction={handleDirectoryChromeAction}
    >
      {content}
    </PublicEditorialChrome>
  );

  if (loading) {
    return renderWithEditorialChrome(
        <div className="min-h-screen bg-white">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8">
            <div className="animate-pulse">
              <div className="flex items-start gap-5 mb-6">
                <div className="w-16 h-16 bg-gray-200 rounded-xl" />
                <div className="flex-1">
                  <div className="h-7 bg-gray-200 rounded w-2/3 mb-2" />
                  <div className="h-4 bg-gray-100 rounded w-1/3" />
                </div>
              </div>
              <div className="flex gap-8">
                <div className="flex-1 space-y-4">
                  <div className="h-56 bg-gray-100 rounded-xl" />
                  <div className="h-32 bg-gray-100 rounded-xl" />
                </div>
                <div className="w-72 h-64 bg-gray-100 rounded-xl hidden lg:block" />
              </div>
            </div>
          </div>
        </div>
    );
  }

  if (error || !listing) {
    return renderWithEditorialChrome(
        <div className="min-h-screen bg-white">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16">
            <motion.div
              className="max-w-md mx-auto text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div
                className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: `${colors.primary}10` }}
              >
                <Building2 className="w-8 h-8" style={{ color: colors.primary }} />
              </div>
              <h1 className="text-xl font-bold text-gray-900 mb-2">No encontrado</h1>
              <p className="text-gray-600 text-sm mb-6">
                {error || 'Este establecimiento no existe o ha sido eliminado'}
              </p>
              <Button asChild size="sm" className="rounded-lg" style={{ backgroundColor: colors.primary }}>
                <Link href="/directorio">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Volver al directorio
                </Link>
              </Button>
            </motion.div>
          </div>
        </div>
    );
  }

  const Icon = TYPE_ICONS[listing.type] ?? Building2;
  const typeLabel = TYPE_LABELS[listing.type];
  const typeColor = TYPE_COLORS[listing.type] ?? colors.primary;
  const updatedDate = formatUpdatedDate(listing.updated_at || listing.created_at);
  const instrumentsHeading = listing.type === 'school'
    ? 'Instrumentos y clases'
    : listing.type === 'store'
      ? 'Instrumentos disponibles'
      : listing.type === 'rehearsal'
        ? 'Instrumentos y equipamiento'
        : 'Instrumentos';

  // Structured data is emitted once by the server route.
  return renderWithEditorialChrome(
    <div className="overflow-x-hidden">
      <div className="min-h-screen bg-white overflow-x-hidden">
        {/* Breadcrumb and Back Button */}
        <div className="border-b border-gray-100 overflow-hidden">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex flex-col gap-2">
              {/* Back Button */}
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors group w-fit"
              >
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
                <span className="hidden sm:inline">Volver a resultados</span>
                <span className="sm:hidden">Volver</span>
              </button>

              {/* Breadcrumb */}
              <nav className="flex items-center gap-1.5 text-xs overflow-hidden">
                <Link href="/directorio" className="text-gray-500 hover:text-gray-700 whitespace-nowrap flex-shrink-0">Directorio</Link>
                <ChevronRight className="w-3 h-3 text-gray-300 flex-shrink-0" />
                <Link href={`/directorio?type=${listing.type}`} className="text-gray-500 hover:text-gray-700 whitespace-nowrap flex-shrink-0">{typeLabel}s</Link>
                <ChevronRight className="w-3 h-3 text-gray-300 flex-shrink-0" />
                <span className="text-gray-900 font-medium truncate min-w-0">{listing.name}</span>
              </nav>
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="bg-gray-50 border-b border-gray-100">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-5 sm:py-8">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-5"
            >
              {/* Logo or Icon - smaller on mobile */}
              {listing.logo_url ? (
                <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl overflow-hidden flex-shrink-0 border border-gray-200 bg-white">
                  <img
                    src={listing.logo_url}
                    alt={`Logo de ${listing.name}`}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      // Fallback to icon if logo fails to load
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      target.parentElement!.innerHTML = `<div class="w-full h-full flex items-center justify-center" style="background-color: ${typeColor}12"><svg class="w-7 h-7 sm:w-10 sm:h-10" style="color: ${typeColor}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path></svg></div>`;
                    }}
                  />
                </div>
              ) : (
                <div
                  className="w-14 h-14 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${typeColor}12` }}
                >
                  <Icon className="w-7 h-7 sm:w-10 sm:h-10" style={{ color: typeColor }} />
                </div>
              )}

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2.5 mb-2">
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{listing.name}</h1>
                  {listing.is_premium && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold bg-amber-100 text-amber-700">
                      <Crown className="w-3 h-3" /> PREMIUM
                    </span>
                  )}
                  {listing.rating && listing.rating >= 4.5 && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold bg-purple-100 text-purple-700">
                      <Award className="w-3 h-3" /> TOP
                    </span>
                  )}
                  {listing.is_verified && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold bg-blue-100 text-blue-600">
                      <CheckCircle className="w-3 h-3" /> Verificado
                    </span>
                  )}
                  {listing.is_claimed && (
                    <Link
                      href={`${getAppUrl()}/directorio/${listing.slug}/badge`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold text-purple-600 underline decoration-dotted underline-offset-2 hover:text-purple-700"
                    >
                      <BadgeCheck className="w-3 h-3" /> Consigue tu badge para tu web
                    </Link>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mt-1">
                  <span className="font-medium text-gray-600">{listing.category || typeLabel}</span>
                  <span>·</span>
                  <span className="flex items-center gap-1.5">
                    <MapPin className="w-4 h-4" />
                    {listing.city}
                  </span>
                  {listing.rating && (
                    <>
                      <span>·</span>
                      <span className="flex items-center gap-1.5">
                        <Star className="w-4 h-4 text-amber-400 fill-current" />
                        {listing.rating} ({listing.review_count} reseñas)
                      </span>
                    </>
                  )}
                  <span>·</span>
                  <span className="flex items-center gap-1.5">
                    <Eye className="w-4 h-4" />
                    {getDisplayViewCount(listing.id, listing.view_count)} visitas
                  </span>
                </div>
              </div>

              {/* Actions - Mobile: full width row below, Desktop: inline */}
              <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto mt-4 sm:mt-0">
                {/* Mobile enquiry button — scrolls to the contact form where enabled. */}
                {(listing.type === 'school' || listing.type === 'rehearsal' || listing.type === 'hub') && (
                  <Button
                    size="sm"
                    className="rounded-lg h-10 gap-1.5 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white shadow-md flex-1 lg:hidden"
                    onClick={() => {
                      trackClick('booking_header_click');
                      const bookingCard = document.getElementById('booking-card');
                      if (bookingCard) {
                        bookingCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        // Focus first input after scroll
                        setTimeout(() => {
                          const firstInput = bookingCard.querySelector('input');
                          if (firstInput) firstInput.focus();
                        }, 500);
                      }
                    }}
                  >
                    <CalendarDays className="w-4 h-4" />
                    {listing.type === 'rehearsal' ? 'Consultar disponibilidad' : 'Solicitar información'}
                  </Button>
                )}
                {listing.website && (
                  <Button
                    asChild
                    size="sm"
                    className="rounded-lg h-10 flex-1 sm:flex-none"
                    style={{ backgroundColor: typeColor }}
                  >
                    <a
                      href={listing.website.startsWith('http') ? listing.website : `https://${listing.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => interceptExternal(e, listing.website!.startsWith('http') ? listing.website! : `https://${listing.website}`, 'website', 'website')}
                    >
                      Visitar
                      <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
                    </a>
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-lg h-10 gap-1.5 hidden sm:flex"
                  onClick={async () => {
                    const url = window.location.href;
                    try {
                      await navigator.clipboard.writeText(url);
                      trackClick('copy_link');
                      const toast = document.createElement('div');
                      toast.className = 'fixed bottom-4 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-4 py-2 rounded-lg text-sm z-50 animate-in fade-in slide-in-from-bottom-2';
                      toast.textContent = 'Enlace copiado';
                      document.body.appendChild(toast);
                      setTimeout(() => toast.remove(), 2000);
                    } catch (err) {
                      console.error('Copy failed:', err);
                    }
                  }}
                >
                  Copiar enlace
                </Button>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="border-b border-gray-100 overflow-hidden">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <nav className="flex gap-4 sm:gap-6 overflow-x-auto scrollbar-hide">
              <button
                onClick={() => setActiveTab('info')}
                className={`py-3 text-sm font-medium border-b-2 -mb-px transition ${
                  activeTab === 'info'
                    ? 'text-gray-900 border-gray-900'
                    : 'text-gray-500 hover:text-gray-700 border-transparent'
                }`}
              >
                Información
              </button>
              <button
                onClick={() => setActiveTab('fotos')}
                className={`py-3 text-sm font-medium border-b-2 -mb-px transition ${
                  activeTab === 'fotos'
                    ? 'text-gray-900 border-gray-900'
                    : 'text-gray-500 hover:text-gray-700 border-transparent'
                }`}
              >
                Fotos
              </button>
              <button
                onClick={() => setActiveTab('horarios')}
                className={`py-3 text-sm font-medium border-b-2 -mb-px transition ${
                  activeTab === 'horarios'
                    ? 'text-gray-900 border-gray-900'
                    : 'text-gray-500 hover:text-gray-700 border-transparent'
                }`}
              >
                {listing.type === 'store' ? 'Horario de apertura' : 'Horarios'}
              </button>
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Left Column */}
            <div className="flex-1 min-w-0 space-y-6">

              {/* Tab: Información */}
              {activeTab === 'info' && (
                <>
                  {/* Quick Actions */}
                  <div className="flex flex-wrap gap-2">
                    {listing.phone && (
                      <a
                        href={`tel:${listing.phone}`}
                        onClick={(e) => interceptExternal(e, `tel:${listing.phone}`, 'phone', 'phone')}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm transition"
                      >
                        <Phone className="w-3.5 h-3.5" />
                        {listing.phone}
                      </a>
                    )}
                    {(listing.whatsapp || listing.phone) && (
                      <a
                        href={`https://wa.me/34${(listing.whatsapp || listing.phone || '').replace(/\D/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => interceptExternal(e, `https://wa.me/34${(listing.whatsapp || listing.phone || '').replace(/\D/g, '')}`, 'whatsapp', 'whatsapp')}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-100 hover:bg-green-200 text-green-700 text-sm transition"
                      >
                        <MessageCircle className="w-3.5 h-3.5" />
                        WhatsApp
                      </a>
                    )}
                    {listing.email && (
                      <a
                        href={`mailto:${listing.email}`}
                        onClick={(e) => interceptExternal(e, `mailto:${listing.email}`, 'email', 'email')}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm transition"
                      >
                        <Mail className="w-3.5 h-3.5" />
                        Email
                      </a>
                    )}
                    {listing.instagram && (
                      <a
                        href={listing.instagram.startsWith('http') ? listing.instagram : `https://instagram.com/${listing.instagram.replace('@', '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => interceptExternal(e, listing.instagram!.startsWith('http') ? listing.instagram! : `https://instagram.com/${listing.instagram!.replace('@', '')}`, 'instagram', 'instagram')}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm transition"
                      >
                        <Instagram className="w-3.5 h-3.5" />
                        Instagram
                      </a>
                    )}
                    {listing.facebook && (
                      <a
                        href={listing.facebook.startsWith('http') ? listing.facebook : `https://facebook.com/${listing.facebook}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => interceptExternal(e, listing.facebook!.startsWith('http') ? listing.facebook! : `https://facebook.com/${listing.facebook}`, 'facebook', 'facebook')}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm transition"
                      >
                        <Facebook className="w-3.5 h-3.5" />
                        Facebook
                      </a>
                    )}
                    {listing.youtube && (
                      <a
                        href={listing.youtube.startsWith('http') ? listing.youtube : `https://youtube.com/${listing.youtube}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => interceptExternal(e, listing.youtube!.startsWith('http') ? listing.youtube! : `https://youtube.com/${listing.youtube}`, 'youtube', 'youtube')}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-100 hover:bg-red-200 text-red-700 text-sm transition"
                      >
                        <Youtube className="w-3.5 h-3.5" />
                        YouTube
                      </a>
                    )}
                    {listing.google_maps_url && (
                      <a
                        href={listing.google_maps_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => interceptExternal(e, listing.google_maps_url!, 'maps', 'maps')}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm transition"
                      >
                        <MapPin className="w-3.5 h-3.5" />
                        Cómo llegar
                      </a>
                    )}
                  </div>

                  {/* Instruments */}
                  {listing.instruments && listing.instruments.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">{instrumentsHeading}</h3>
                      <div className="flex flex-wrap gap-2">
                        {listing.instruments.map((instrument) => (
                          <span
                            key={instrument}
                            className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium"
                            style={{ backgroundColor: `${typeColor}10`, color: typeColor }}
                          >
                            {INSTRUMENT_LABELS[instrument.toLowerCase()] || instrument}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {listing.services && listing.services.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">Servicios</h3>
                      <div className="flex flex-wrap gap-2">
                        {listing.services.map((service) => (
                          <span key={service} className="inline-flex items-center rounded-md bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                            {service}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Price Range */}
                  {hasRecentPriceEvidence(listing) && (
                    <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200">
                      <span className="text-sm text-amber-800">
                        Precios orientativos: <strong>{listing.price_range}</strong>
                      </span>
                    </div>
                  )}

                  {/* Description */}
                  {listing.observations && (
                    <div>
                      <p className="text-gray-600 leading-relaxed">{listing.observations}</p>
                    </div>
                  )}

                  {/* These summaries stay visible even when a listing has no data yet. */}
                  <section aria-labelledby="directory-hours-summary" className="rounded-xl bg-gray-50 p-4">
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <h3 id="directory-hours-summary" className="text-sm font-semibold text-gray-900">Horario</h3>
                      {hasListingHours(listing.hours) && (
                        <button
                          type="button"
                          onClick={() => setActiveTab('horarios')}
                          className="text-xs font-medium text-gray-500 transition hover:text-gray-800"
                        >
                          Ver detalle
                        </button>
                      )}
                    </div>
                    {hasListingHours(listing.hours) ? (
                      <DirectoryHoursSchedule hours={listing.hours} />
                    ) : (
                      <p className="text-sm text-gray-500">Este establecimiento aún no ha publicado su horario.</p>
                    )}
                  </section>

                  {/* Map */}
                  {listing.latitude && listing.longitude && (
                    <a
                      href={listing.google_maps_url || `https://www.google.com/maps?q=${listing.latitude},${listing.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => interceptExternal(e, listing.google_maps_url || `https://www.google.com/maps?q=${listing.latitude},${listing.longitude}`, 'maps', 'maps')}
                      className="block relative rounded-xl overflow-hidden group h-[220px] sm:h-[280px]"
                    >
                      <img
                        src={`https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/pin-l+${typeColor.replace('#', '')}(${listing.longitude},${listing.latitude})/${listing.longitude},${listing.latitude},15,0/700x400@2x?access_token=${MAPBOX_TOKEN}`}
                        alt={`Ubicación de ${listing.name}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors" />
                      <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-lg shadow text-sm">
                        <p className="font-medium text-gray-900">{listing.address}</p>
                        <p className="text-xs text-gray-500">{listing.city}, {listing.province}</p>
                      </div>
                    </a>
                  )}

                  {/* Photos preview, with a clear empty state for unpopulated listings. */}
                  <section aria-labelledby="directory-photos-summary">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <h3 id="directory-photos-summary" className="text-sm font-semibold text-gray-900">Fotos</h3>
                      {(listing.photos?.length || 0) > 0 && (
                        <button
                          type="button"
                          onClick={() => setActiveTab('fotos')}
                          className="text-xs font-medium text-gray-500 transition hover:text-gray-800"
                        >
                          Ver todas ({listing.photos?.length})
                        </button>
                      )}
                    </div>
                    {(listing.photos?.length || 0) > 0 ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {listing.photos!.slice(0, 3).map((photo, index) => (
                          <div key={index} className="aspect-square rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
                            <DirectoryPhoto
                              url={photo.url}
                              alt={photo.alt || photo.caption || `Foto ${index + 1} de ${listing.name}`}
                              className="w-full h-full"
                              imgClassName="w-full h-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="rounded-xl bg-gray-50 px-4 py-3 text-sm text-gray-500">Este establecimiento aún no ha publicado fotos.</p>
                    )}
                  </section>

                  {/* Claim CTA */}
                  {!listing.is_claimed && (
                    <button
                      onClick={() => {
                        setClaimModalOpen(true);
                        trackClick('claim_click');
                      }}
                      className="group flex w-full items-center justify-between rounded-xl p-4 text-left transition-all hover:shadow-lg bg-white border-3"
                      style={{ borderColor: '#4F46E5', borderWidth: '3px' }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ backgroundColor: '#4F46E5' }}>
                          <BadgeCheck className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <p className="text-sm font-bold" style={{ color: '#4F46E5' }}>¿Es tu negocio?</p>
                          <p className="text-xs text-gray-500">Recláma­la gratis y muestra tu contacto</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-semibold transition-transform group-hover:scale-105 text-white" style={{ backgroundColor: '#4F46E5' }}>
                        Reclamar
                        <ArrowRight className="h-4 w-4" />
                      </div>
                    </button>
                  )}

                  {/* Meta info */}
                  <div className="flex items-center gap-4 text-xs text-gray-400 pt-2">
                    {updatedDate && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        Actualizado {updatedDate}
                      </span>
                    )}
                    <Link
                      href="/directorio"
                      className="flex items-center gap-1 hover:text-gray-600 transition"
                    >
                      <ArrowLeft className="w-3.5 h-3.5" />
                      Directorio
                    </Link>
                  </div>
                </>
              )}

              {/* Tab: Fotos */}
              {activeTab === 'fotos' && (
                <>
                  {listing.photos && listing.photos.length > 0 ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {listing.photos.map((photo, index) => (
                          <a
                            key={index}
                            href={photo.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="relative aspect-[4/3] rounded-xl overflow-hidden group"
                          >
                            <DirectoryPhoto
                              url={photo.url}
                              alt={photo.alt || `${listing.name} - Foto ${index + 1}`}
                              className="w-full h-full"
                              imgClassName="w-full h-full object-cover transition-transform group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                            {photo.type && (
                              <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded text-xs font-medium bg-black/50 text-white">
                                {photo.type === 'exterior' ? 'Exterior' :
                                 photo.type === 'interior' ? 'Interior' :
                                 photo.type === 'class' ? 'Clases' :
                                 photo.type === 'team' ? 'Equipo' : ''}
                              </span>
                            )}
                          </a>
                        ))}
                      </div>
                      <p className="text-xs text-gray-400 text-center">
                        {listing.photos.length} foto{listing.photos.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  ) : (
                    <div className="py-12 text-center">
                      <div
                        className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                        style={{ backgroundColor: `${typeColor}10` }}
                      >
                        <Camera className="w-8 h-8" style={{ color: typeColor }} />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Fotos próximamente</h3>
                      <p className="text-sm text-gray-500 max-w-sm mx-auto">
                        {listing.is_claimed
                          ? 'El propietario aún no ha subido fotos de su establecimiento.'
                          : 'Cuando el propietario gestione este perfil, podrá subir fotos de su establecimiento.'}
                      </p>
                      {!listing.is_claimed ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-4 border-purple-200 text-purple-700 hover:bg-purple-50"
                          onClick={() => {
                            setClaimModalOpen(true);
                            trackClick('claim_from_fotos');
                          }}
                        >
                          ¿Es tuyo? Verifícalo gratis
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-4"
                          onClick={() => {
                            setActiveTab('info');
                            trackClick('deadend_fotos_to_info');
                          }}
                        >
                          Ver información de contacto
                        </Button>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Tab: Horarios */}
              {activeTab === 'horarios' && (
                <>
                  {hasListingHours(listing.hours) ? (
                    <div className="space-y-4">
                      <div className="bg-gray-50 rounded-xl p-4">
                        <DirectoryHoursSchedule hours={listing.hours} />
                      </div>
                      <p className="text-xs text-gray-400 text-center">
                        Los horarios pueden variar. Contacta para confirmar.
                      </p>
                    </div>
                  ) : (
                    <div className="py-12 text-center">
                      <div
                        className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                        style={{ backgroundColor: `${typeColor}10` }}
                      >
                        <Clock className="w-8 h-8" style={{ color: typeColor }} />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Horarios próximamente</h3>
                      <p className="text-sm text-gray-500 max-w-sm mx-auto">
                        {listing.is_claimed
                          ? 'El propietario aún no ha configurado los horarios de apertura.'
                          : 'Cuando el propietario gestione este perfil, podrá agregar sus horarios de apertura.'}
                      </p>
                      {!listing.is_claimed ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-4 border-purple-200 text-purple-700 hover:bg-purple-50"
                          onClick={() => {
                            setClaimModalOpen(true);
                            trackClick('claim_from_horarios');
                          }}
                        >
                          ¿Es tuyo? Verifícalo gratis
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-4"
                          onClick={() => {
                            setActiveTab('info');
                            trackClick('deadend_horarios_to_info');
                          }}
                        >
                          Ver información de contacto
                        </Button>
                      )}
                    </div>
                  )}
                </>
              )}

            </div>

            {/* Sidebar - Booking/Airbnb style */}
            <aside className="w-full lg:w-80 flex-shrink-0 lg:border-l lg:border-gray-100 lg:pl-8 mt-8 lg:mt-0">
              <div className="lg:sticky lg:top-24 space-y-8 lg:space-y-5">
                {/* Hubs accept an information request; none of these forms promises a booking. */}
                {(listing.type === 'school' || listing.type === 'rehearsal' || listing.type === 'hub') && (
                  <div id="booking-card" className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
                    {/* Header */}
                    <div className="p-4 bg-gradient-to-r from-emerald-500 to-emerald-600">
                      <div className="flex items-center gap-2 text-white">
                        <CalendarDays className="w-5 h-5" />
                        <span className="font-semibold">
                          {listing.type === 'rehearsal' ? 'Consultar disponibilidad' : 'Solicitar información'}
                        </span>
                      </div>
                      {hasRecentPriceEvidence(listing) && (
                        <p className="text-emerald-100 text-sm mt-1">
                          {listing.price_range}
                        </p>
                      )}
                    </div>

                    {/* Form Body */}
                    <div className="p-4">
                      {bookingSuccess ? (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          data-routing-mode={bookingRoutingMode || undefined}
                          data-delivery-status={bookingDeliveryStatus || undefined}
                          className="py-4 text-center"
                        >
                          <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                            <CheckCircle className="w-6 h-6 text-emerald-600" />
                          </div>
                          <h3 className="text-base font-bold text-gray-900 mb-1">¡Consulta enviada!</h3>
                          <p className="text-sm text-gray-600 mb-4">
                            {bookingDeliveryStatus === 'sent'
                              ? `Hemos enviado tu consulta a ${listing.name}. No confirma una reserva.`
                              : bookingDeliveryStatus === 'queued'
                                ? `Hemos recibido tu consulta y está preparada para enviarse a ${listing.name}. No confirma una reserva.`
                                : 'Hemos recibido tu consulta para revisarla antes de contactar con el establecimiento. No confirma una reserva.'}
                          </p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setBookingSuccess(false);
                              setBookingRoutingMode(null);
                              setBookingDeliveryStatus(null);
                              setBookingForm({ name: '', email: '', phone: '', interest: '', message: '' });
                            }}
                          >
                            Enviar otra solicitud
                          </Button>
                        </motion.div>
                      ) : (
                        <form onSubmit={handleBookingSubmit} onFocus={() => trackFormStarted('booking')} className="space-y-3">
                          <p className="text-sm leading-relaxed text-gray-600">
                            {listing.is_claimed
                              ? `Tu consulta va dirigida a ${listing.name}. Jamsom confirmará el canal adecuado antes de facilitarle tus datos.`
                              : 'Cuéntanos qué buscas y te pediremos autorización antes de facilitar el contacto.'}
                          </p>
                          <div className="space-y-1">
                            <Label htmlFor="booking-name" className="text-sm">Nombre</Label>
                            <Input
                              id="booking-name"
                              name="name"
                              autoComplete="name"
                              maxLength={120}
                              placeholder="Tu nombre"
                              value={bookingForm.name}
                              onChange={(e) => setBookingForm({ ...bookingForm, name: e.target.value })}
                              required
                              className="h-10 text-sm"
                            />
                          </div>

                          <div className="space-y-1">
                            <Label htmlFor="booking-email" className="text-sm">Email</Label>
                            <Input
                              id="booking-email"
                              name="email"
                              type="email"
                              inputMode="email"
                              autoComplete="email"
                              maxLength={254}
                              placeholder="tu@email.com"
                              value={bookingForm.email}
                              onChange={(e) => setBookingForm({ ...bookingForm, email: e.target.value })}
                              required
                              className="h-10 text-sm"
                            />
                          </div>

                          <div className="space-y-1">
                            <Label htmlFor="booking-phone" className="text-sm">Teléfono <span className="font-normal text-gray-500">(opcional)</span></Label>
                            <Input
                              id="booking-phone"
                              name="phone"
                              type="tel"
                              inputMode="tel"
                              autoComplete="tel"
                              maxLength={40}
                              placeholder="WhatsApp o teléfono"
                              value={bookingForm.phone}
                              onChange={(e) => setBookingForm({ ...bookingForm, phone: e.target.value })}
                              className="h-10 text-sm"
                            />
                          </div>

                          {listing.type === 'school' && <div className="space-y-1">
                            <Label htmlFor="booking-interest" className="text-sm">Instrumento o interés</Label>
                            <Input
                              id="booking-interest"
                              name="interest"
                              maxLength={160}
                              placeholder="Piano, canto, clases para mi hija…"
                              value={bookingForm.interest}
                              onChange={(e) => setBookingForm({ ...bookingForm, interest: e.target.value })}
                              required
                              className="h-10 text-sm"
                            />
                          </div>}

                          <div className="space-y-1">
                            <Label htmlFor="booking-message" className="text-sm">Mensaje <span className="font-normal text-gray-500">(opcional)</span></Label>
                            <Textarea
                              id="booking-message"
                              name="message"
                              maxLength={2000}
                              placeholder={listing.type === 'rehearsal' ? 'Fechas, horario o necesidades…' : 'Añade cualquier detalle útil…'}
                              value={bookingForm.message}
                              onChange={(e) => setBookingForm({ ...bookingForm, message: e.target.value })}
                              rows={2}
                              className="text-sm"
                            />
                            <p className="text-xs leading-relaxed text-amber-800">
                              No incluyas datos médicos ni datos personales de menores.
                            </p>
                          </div>

                          {bookingError && <p id="booking-error" role="alert" aria-live="polite" className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{bookingError}</p>}

                          <Button
                            type="submit"
                            className="w-full min-h-11 h-auto px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm leading-tight font-semibold whitespace-normal rounded-lg"
                            disabled={bookingSubmitting}
                            aria-describedby={bookingError ? 'booking-error' : undefined}
                            aria-label={`Enviar consulta a ${listing.name}. Solicitar información`}
                            onClick={() => trackClick('booking_sidebar_submit')}
                          >
                            {bookingSubmitting ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Enviando...
                              </>
                            ) : (
                              <>Enviar consulta a {listing.name}</>
                            )}
                          </Button>

                          <p className="text-xs leading-relaxed text-gray-500 text-center">
                            {listing.is_claimed
                              ? <>Al enviarla, autorizas a Jamsom Music a compartir tus datos de contacto con {listing.name} para que pueda responderte. No usaremos esta consulta para marketing.</>
                              : <>Jamsom Music no gestiona esta ficha ni usará esta consulta para marketing. Antes de enviarla te pediremos autorización para compartir tus datos de contacto.</>}{' '}
                            <Link href="/privacidad" className="font-medium underline underline-offset-2 hover:text-gray-700">
                              Política de privacidad
                            </Link>
                          </p>

                          <div className="flex items-center justify-center gap-3 text-xs text-gray-400 pt-1">
                            <span className="flex items-center gap-1">
                              <CheckCircle className="w-3 h-3 text-emerald-500" />
                              Sin compromiso
                            </span>
                          </div>
                        </form>
                      )}
                    </div>

                    {/* Quick contact - Only show if verified */}
                    {listing.is_claimed && (listing.phone || listing.whatsapp) && (
                      <div className="px-4 pb-4">
                        <div className="flex gap-2">
                          {listing.phone && (
                            <a
                              href={`tel:${listing.phone}`}
                              onClick={(e) => interceptExternal(e, `tel:${listing.phone}`, 'phone', 'phone_sidebar')}
                              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 transition"
                            >
                              <Phone className="w-3.5 h-3.5" />
                              Llamar
                            </a>
                          )}
                          {(listing.whatsapp || listing.phone) && (
                            <a
                              href={`https://wa.me/34${(listing.whatsapp || listing.phone || '').replace(/\D/g, '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => interceptExternal(e, `https://wa.me/34${(listing.whatsapp || listing.phone || '').replace(/\D/g, '')}`, 'whatsapp', 'whatsapp_sidebar')}
                              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700 hover:bg-green-100 transition"
                            >
                              <MessageCircle className="w-3.5 h-3.5" />
                              WhatsApp
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <Dialog
                  open={sharingAuthorizationOpen}
                  onOpenChange={(open) => {
                    if (!bookingSubmitting) setSharingAuthorizationOpen(open);
                  }}
                >
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>Autoriza el contacto con {listing.name}</DialogTitle>
                      <DialogDescription className="leading-relaxed">
                        {listing.name} no gestiona esta ficha en Jamsom Music. Si confirmas, autorizas a Jamsom Music a compartir tu nombre, email, teléfono opcional y consulta con el establecimiento en tu nombre para que pueda responderte.
                      </DialogDescription>
                    </DialogHeader>
                    <p className="text-sm leading-relaxed text-gray-600">
                      Esta autorización solo se utiliza para esta consulta; no implica comunicaciones de marketing. No incluyas datos médicos ni datos personales de menores.{' '}
                      <Link href="/privacidad" className="font-medium underline underline-offset-2 hover:text-gray-900">
                        Consulta la política de privacidad
                      </Link>
                      .
                    </p>
                    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                      <Button
                        type="button"
                        variant="outline"
                        disabled={bookingSubmitting}
                        onClick={() => setSharingAuthorizationOpen(false)}
                      >
                        Cancelar
                      </Button>
                      <Button
                        type="button"
                        disabled={bookingSubmitting}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => void submitBooking()}
                      >
                        {bookingSubmitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Enviando...</> : 'Confirmar y enviar'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* B2C Banner - Always visible */}
                <DirectoryPromoBannerB2C onTrack={trackClick} />

                {/* Other Listings - Now below the banners */}
                {relatedListings.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        {listing.type === 'school' ? 'Otras escuelas' :
                         listing.type === 'rehearsal' ? 'Otros locales' :
                         listing.type === 'store' ? 'Otras tiendas' :
                         listing.type === 'club' ? 'Otros clubes' : 'Otros'}
                      </h2>
                      <Link
                        href={`/directorio?type=${listing.type}&city=${listing.city}`}
                        className="text-xs font-medium flex items-center gap-0.5 text-gray-500 hover:text-gray-700"
                      >
                        Ver todos <ArrowRight className="w-3 h-3" />
                      </Link>
                    </div>

                    <div className="space-y-3">
                      {relatedListings.map((related) => {
                        const RelatedIcon = TYPE_ICONS[related.type as keyof typeof TYPE_ICONS] || Building2;
                        const relatedColor = TYPE_COLORS[related.type] || colors.primary;

                        return (
                          <Link
                            key={related.id}
                            href={`/directorio/${related.slug}`}
                            className="flex items-start gap-3 group"
                          >
                            <div
                              className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 border border-gray-100"
                              style={{ backgroundColor: `${relatedColor}08` }}
                            >
                              <RelatedIcon className="w-5 h-5" style={{ color: relatedColor }} />
                            </div>
                            <div className="flex-1 min-w-0 pt-0.5">
                              <p className="text-sm font-medium text-gray-900 group-hover:text-gray-700 line-clamp-1">
                                {related.name}
                              </p>
                              <p className="text-xs text-gray-500 line-clamp-1">
                                {TYPE_LABELS[related.type] || 'Establecimiento'}
                              </p>
                              <div className="flex items-center gap-2 mt-1">
                                {related.rating && (
                                  <span className="flex items-center gap-0.5 text-xs text-gray-500">
                                    <Star className="w-3 h-3 text-amber-400 fill-current" />
                                    {related.rating}
                                  </span>
                                )}
                                {related.is_claimed && (
                                  <span className="flex items-center gap-0.5 text-xs text-blue-600">
                                    <CheckCircle className="w-3 h-3" />
                                    Verificado
                                  </span>
                                )}
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Empty state for sidebar when no related */}
                {relatedListings.length === 0 && (
                  <div className="text-center py-6">
                    <div
                      className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-3 border border-gray-100"
                      style={{ backgroundColor: `${typeColor}08` }}
                    >
                      <Building2 className="w-5 h-5" style={{ color: typeColor }} />
                    </div>
                    <p className="text-sm text-gray-500">
                      No hay otros {typeLabel.toLowerCase()}s en {listing.city}
                    </p>
                    <Link
                      href={`/directorio?type=${listing.type}`}
                      className="text-sm font-medium mt-2 inline-flex items-center gap-1"
                      style={{ color: typeColor }}
                    >
                      Ver en otras ciudades <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                )}
              </div>
            </aside>
          </div>

          {/* B2B Banner - Full-width, only for school/rehearsal */}
          {(listing.type === 'school' || listing.type === 'rehearsal') && (
            <div className="mt-8 pb-8">
              <DirectoryPromoBannerB2B
                type={listing.type}
                onTrack={trackClick}
              />
            </div>
          )}
        </div>
      </div>

      {/* Claim Modal */}
      <Dialog open={claimModalOpen || claimRequestedFromLink} onOpenChange={handleClaimModalOpenChange}>
        <DialogContent className="!max-w-3xl !gap-0 overflow-hidden !p-0">
          {claimSuccess ? (
            <div className="px-8 py-10 text-center">
              <div
                className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full"
                style={{ backgroundColor: `${typeColor}15` }}
              >
                <CheckCircle className="h-6 w-6" style={{ color: typeColor }} />
              </div>
              <h3 className="mb-1 font-semibold text-gray-900">Solicitud enviada</h3>
              <p className="text-sm text-gray-500">
                Hemos guardado tu solicitud. La revisaremos antes de aprobar la verificación y te contactaremos por los datos indicados.
              </p>
              <Button
                className="mt-4"
                variant="outline"
                onClick={() => {
                  handleClaimModalOpenChange(false);
                  setClaimSuccess(false);
                  setClaimForm({ name: '', email: '', phone: '', message: '' });
                }}
              >
                Cerrar
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2">
              {/* LEFT: benefits */}
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-6 md:p-7">
                <DialogHeader className="mb-4 space-y-1 text-left">
                  <DialogTitle className="text-xl">Verifica tu negocio</DialogTitle>
                  <p className="text-sm text-gray-500">
                    Confirma que eres el propietario de <span className="font-medium text-gray-700">{listing.name}</span>.
                  </p>
                </DialogHeader>
                <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <Sparkles className="h-4 w-4 text-purple-500" />
                  Al verificar, desbloqueas
                </h4>
                <ul className="space-y-2.5 text-sm text-gray-600">
                  {[
                    'Muestra tu teléfono, web y horarios',
                    'Badge de «Verificado» en tu perfil',
                    'Recibe consultas directas de músicos',
                    'Estadísticas de visitas a tu ficha',
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-2">
                      <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
                      {t}
                    </li>
                  ))}
                </ul>
                <p className="mt-5 text-xs text-gray-400">Gratis · Revisamos cada solicitud antes de aprobar.</p>
              </div>

              {/* RIGHT: form */}
              <form onSubmit={handleClaimSubmit} onFocus={() => trackFormStarted('claim')} className="flex flex-col gap-3 p-6 md:p-7">
                <div className="space-y-1.5">
                  <Label htmlFor="claim-name">Tu nombre</Label>
                  <Input
                    id="claim-name"
                    name="name"
                    autoComplete="name"
                    maxLength={120}
                    placeholder="María García"
                    value={claimForm.name}
                    onChange={(e) => setClaimForm({ ...claimForm, name: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="claim-email">Email de contacto</Label>
                  <Input
                    id="claim-email"
                    name="email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    maxLength={254}
                    placeholder="maria@tuescuela.com"
                    value={claimForm.email}
                    onChange={(e) => setClaimForm({ ...claimForm, email: e.target.value })}
                    required
                  />
                  <p className="text-[11px] text-gray-400">Usa el email de tu negocio: acelera la verificación.</p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="claim-phone">Teléfono</Label>
                  <Input
                    id="claim-phone"
                    name="phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    maxLength={40}
                    placeholder="+34 612 345 678"
                    value={claimForm.phone}
                    onChange={(e) => setClaimForm({ ...claimForm, phone: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="claim-message" className="text-gray-500">
                    ¿Algo que nos ayude a verificarte? <span className="font-normal">(opcional)</span>
                  </Label>
                  <Textarea
                    id="claim-message"
                    name="message"
                    maxLength={2000}
                    placeholder="Soy el director/propietario desde 2015…"
                    value={claimForm.message}
                    onChange={(e) => setClaimForm({ ...claimForm, message: e.target.value })}
                    rows={2}
                  />
                </div>

                {claimError && <p id="claim-error" role="alert" aria-live="polite" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{claimError}</p>}

                <div className="mt-1 flex gap-3">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => handleClaimModalOpenChange(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="flex-1 bg-purple-600 hover:bg-purple-700" disabled={claimSubmitting} aria-describedby={claimError ? 'claim-error' : undefined}>
                    {claimSubmitting ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Enviando…</>
                    ) : (
                      'Enviar verificación'
                    )}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de captura de email antes de salir a un CTA externo (leak-capture) */}
      <LeadCaptureRedirectModal
        open={!!leakRedirect}
        onOpenChange={(o) => { if (!o) setLeakRedirect(null); }}
        listing={{ id: listing.id, name: listing.name, slug: listing.slug, city: listing.city }}
        redirect={leakRedirect}
        colors={colors}
      />
    </div>
  );
}
